import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, LifeBuoy, Loader2, Send } from "lucide-react";
import { API } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const STATUS_META = {
  open: { label: "Offen", cls: "bg-amber-500/10 text-amber-300" },
  answered: { label: "Beantwortet", cls: "bg-emerald-500/10 text-emerald-300" },
  closed: { label: "Geschlossen", cls: "bg-zinc-500/10 text-zinc-400" },
};
const STATUSES = ["open", "answered", "closed"];

const avatarHead = (name) =>
  `https://www.habbo.de/habbo-imaging/avatarimage?user=${encodeURIComponent(name)}&direction=2&head_direction=3&size=m&headonly=1`;

const fmtDate = (iso) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const SupportPanel = ({ token }) => {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);

  const load = async (status = filter) => {
    try {
      const r = await axios.get(`${API}/cms/support`, { ...auth, params: status ? { status } : {} });
      setData(r.data);
    } catch { setData({ items: [], counts: {} }); }
  };
  useEffect(() => { load(filter); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendReply = async (t) => {
    if (!reply.trim()) return;
    setReplying(true);
    try {
      await axios.post(`${API}/support/${t.id}/messages`, { text: reply.trim() }, auth);
      setReply("");
      toast({ title: "Antwort gesendet", description: `${t.habbo_name} wurde im Postfach benachrichtigt.` });
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({ title: "Fehler", description: typeof d === "string" ? d : "Antwort fehlgeschlagen", variant: "destructive" });
    } finally { setReplying(false); }
  };

  const setStatus = async (t, status) => {
    try {
      await axios.patch(`${API}/cms/support/${t.id}`, { status }, auth);
      await load();
    } catch { toast({ title: "Fehler", description: "Status-Wechsel fehlgeschlagen", variant: "destructive" }); }
  };

  if (data === null) return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;

  return (
    <div data-testid="support-panel">
      <h2 className="flex items-center gap-2 text-xl font-bold text-white"><LifeBuoy className="h-5 w-5 text-[#9d7bff]" /> Support-Anfragen</h2>
      <p className="mt-0.5 text-xs text-zinc-500">Antworten landen im Habthek-Postfach des Users.</p>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => setFilter(null)} data-testid="support-panel-filter-all" className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${!filter ? "bg-[#7c4dff] text-white" : "border border-white/10 bg-white/5 text-zinc-400 hover:text-white"}`}>Alle</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s === filter ? null : s)} data-testid={`support-panel-filter-${s}`} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${filter === s ? "bg-[#7c4dff] text-white" : "border border-white/10 bg-white/5 text-zinc-400 hover:text-white"}`}>
            {STATUS_META[s].label} ({data.counts?.[s] ?? 0})
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {data.items.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-[#0f141b] px-4 py-10 text-center text-sm text-zinc-500" data-testid="support-panel-empty">Keine Anfragen{filter ? " mit diesem Status" : ""}.</div>
        )}
        {data.items.map((t) => {
          const meta = STATUS_META[t.status] || STATUS_META.open;
          const isOpen = expanded === t.id;
          return (
            <div key={t.id} className="overflow-hidden rounded-xl border border-white/10 bg-[#0f141b]" data-testid={`support-panel-ticket-${t.id}`}>
              <button onClick={() => { setExpanded(isOpen ? null : t.id); setReply(""); }} data-testid={`support-panel-toggle-${t.id}`} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03]">
                <img src={avatarHead(t.habbo_name)} alt={t.habbo_name} className="h-9 w-9 shrink-0" style={{ imageRendering: "pixelated" }} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-white">{t.subject}</div>
                  <div className="text-[11px] text-zinc-500">{t.habbo_name} · {fmtDate(t.updated_at)} · {t.messages?.length || 0} Nachricht(en)</div>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${meta.cls}`}>{meta.label}</span>
                <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="border-t border-white/5 px-4 py-4">
                  <div className="space-y-2">
                    {(t.messages || []).map((m) => (
                      <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${m.is_team ? "bg-[#7c4dff]/10 text-zinc-200" : "bg-white/5 text-zinc-300"}`}>
                        <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                          {m.is_team ? `Team (${m.author})` : m.author} · {fmtDate(m.created_at)}
                        </div>
                        <div className="whitespace-pre-wrap">{m.text}</div>
                      </div>
                    ))}
                  </div>
                  {t.status !== "closed" && (
                    <div className="mt-3 flex gap-2">
                      <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Als Team antworten…" data-testid={`support-panel-reply-input-${t.id}`} className="bg-[#10141b] border-white/10 text-zinc-200" />
                      <Button onClick={() => sendReply(t)} disabled={replying || !reply.trim()} data-testid={`support-panel-reply-send-${t.id}`} className="btn-primary rounded-lg px-3 py-2 text-sm">
                        {replying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                      <button key={s} onClick={() => setStatus(t, s)} disabled={t.status === s} data-testid={`support-panel-status-${s}-${t.id}`} className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${t.status === s ? `${STATUS_META[s].cls} cursor-default ring-1 ring-inset ring-white/20` : "border border-white/10 bg-white/5 text-zinc-400 hover:text-white"}`}>
                        {STATUS_META[s].label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SupportPanel;
